import { t } from '../i18n';
// client/admin-client/src/pages/NoticeManagement.js
// client/admin-client/src/pages/NoticeManagement.js
import React, { useEffect, useMemo, useState } from 'react';
import NoticeList from '../components/notice/NoticeList';
import NoticePreview from '../components/notice/NoticePreview';
import NoticeEditorModal from '../components/notice/NoticeEditorModal';
import { createNotice, deleteNotice, fetchAdminNotices, updateNotice, } from '../components/notice/noticeApi';
import { makeEmptyNotice, sortNoticeItems } from '../components/notice/noticeStorage';
export default function NoticeManagement() {
    const [items, setItems] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [query, setQuery] = useState('');
    const [filter, setFilter] = useState('all'); // all | published | draft
    const [modal, setModal] = useState({ open: false, mode: 'create', draft: null });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const me = useMemo(() => {
        try {
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            return {
                level: String(user.user_level || ''),
                writer: user.nickname || user.id || user.user_id || 'admin',
            };
        }
        catch (_) {
            return {
                level: '',
                writer: 'admin',
            };
        }
    }, []);
    const applyItems = (nextItems, preferredSelectedId = null) => {
        const sorted = sortNoticeItems(nextItems);
        setItems(sorted);
        if (preferredSelectedId != null) {
            setSelectedId(String(preferredSelectedId));
            return;
        }
        const stillExists = sorted.some((x) => String(x.id) === String(selectedId));
        setSelectedId(stillExists ? selectedId : (sorted[0]?.id || null));
    };
    useEffect(() => {
        let alive = true;
        (async () => {
            setLoading(true);
            try {
                const loaded = await fetchAdminNotices();
                if (!alive)
                    return;
                const sorted = sortNoticeItems(loaded);
                setItems(sorted);
                setSelectedId(sorted[0]?.id || null);
            }
            catch (err) {
                if (!alive)
                    return;
                console.error('[NoticeManagement] fetchAdminNotices failed:', err);
                alert(t('공지 목록을 불러오지 못했어.\n{message}', { message: err.message || err }));
            }
            finally {
                if (alive)
                    setLoading(false);
            }
        })();
        return () => {
            alive = false;
        };
    }, []);
    const selected = useMemo(() => items.find((x) => String(x.id) === String(selectedId)) || null, [items, selectedId]);
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        let list = items;
        if (filter === 'published')
            list = list.filter((x) => !!x.isPublished);
        if (filter === 'draft')
            list = list.filter((x) => !x.isPublished);
        if (!q)
            return list;
        return list.filter((x) => {
            const hay = `${x.title}\n${x.content}\n${x.writer}\n${x.language}`.toLowerCase();
            return hay.includes(q);
        });
    }, [items, query, filter]);
    const openCreate = () => {
        if (saving || deleting)
            return;
        setModal({
            open: true,
            mode: 'create',
            draft: makeEmptyNotice({ writer: me.writer }),
        });
    };
    const openEdit = (notice) => {
        if (saving || deleting)
            return;
        setModal({
            open: true,
            mode: 'edit',
            draft: { ...notice },
        });
    };
    const closeModal = () => {
        if (saving)
            return;
        setModal({ open: false, mode: 'create', draft: null });
    };
    const onSave = async (draft) => {
        try {
            setSaving(true);
            if (modal.mode === 'create') {
                const saved = await createNotice({
                    ...draft,
                    writer: draft.writer || me.writer,
                });
                const next = [saved, ...items.filter((x) => String(x.id) !== String(saved.id))];
                applyItems(next, saved.id);
            }
            else {
                const saved = await updateNotice(draft.id, draft);
                const next = items.map((x) => (String(x.id) === String(saved.id) ? saved : x));
                applyItems(next, saved.id);
            }
            setModal({ open: false, mode: 'create', draft: null });
        }
        catch (err) {
            console.error('[NoticeManagement] save failed:', err);
            alert(t('공지 저장에 실패했어.\n{message}', { message: err.message || err }));
        }
        finally {
            setSaving(false);
        }
    };
    const onDelete = async (notice) => {
        const ok = window.confirm(t('공지 삭제할까?\n\n- {title}', { title: notice.title }));
        if (!ok)
            return;
        try {
            setDeleting(true);
            await deleteNotice(notice.id);
            const next = items.filter((x) => String(x.id) !== String(notice.id));
            const nextSelectedId = next[0]?.id || null;
            applyItems(next, nextSelectedId);
        }
        catch (err) {
            console.error('[NoticeManagement] delete failed:', err);
            alert(t('공지 삭제에 실패했어.\n{message}', { message: err.message || err }));
        }
        finally {
            setDeleting(false);
        }
    };
    return (<div className="min-h-[calc(100vh-120px)]">
       <div className="mb-4 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
         <div>
           <h1 className="text-xl font-semibold">{t("\uACF5\uC9C0 \uC791\uC131/\uAD00\uB9AC")}</h1>
          <p className="text-sm text-gray-500">{t("superadmin \uC804\uC6A9 UI (admin-server / DB \uC800\uC7A5)")}</p>
         </div>
         <button onClick={openCreate} disabled={saving || deleting} className="px-4 py-2 rounded bg-gray-900 text-white hover:bg-black disabled:opacity-50 disabled:cursor-not-allowed">{t("+ \uC0C8 \uACF5\uC9C0 \uC791\uC131")}</button>
       </div>
 
       {loading ? (<div className="rounded-xl border border-gray-200 bg-white shadow-sm p-10 text-center text-gray-500">{t("\uACF5\uC9C0 \uBAA9\uB85D \uBD88\uB7EC\uC624\uB294 \uC911...")}</div>) : (<div className="flex flex-col lg:flex-row gap-4 lg:gap-6 items-stretch lg:items-start min-w-0">
           {/* Left: list */}
           <div className="w-full lg:w-[360px] lg:shrink-0 min-w-0">
             <NoticeList items={filtered} selectedId={selectedId} query={query} onQueryChange={setQuery} filter={filter} onFilterChange={setFilter} onSelect={(id) => setSelectedId(id)} onQuickCreate={openCreate} onEdit={openEdit}/>
           </div>
 
           {/* Right: preview */}
           <div className="flex-1 min-w-0">
             <NoticePreview notice={selected} onEdit={() => selected && openEdit(selected)} onDelete={() => selected && onDelete(selected)}/>
           </div>
         </div>)}
 
       <NoticeEditorModal open={modal.open} mode={modal.mode} draft={modal.draft} onClose={closeModal} onSave={onSave} saving={saving}/>
    </div>);
}
